import {
    computed,
    inject,
    Injectable,
    Injector,
    signal,
} from "@angular/core";

import {
    CustomForm,
    CustomFormControl,
    CustomFormControlProps,
    CustomFormGroup,
} from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import { CashregisterStockFormFields } from "../models/cashregister-stock-model";
import { TranslateService } from "@ngx-translate/core";

import {
    ConfigurationManagerService,
    EntityDictionariesStoreService,
    EntityManagerService,
    FilterStartingWith,
    KeyboardDimension,
    ScreenOrientationService,
    StockDictionaryKey,
} from "src/app/core";

import {
    ItemsByCategory,
} from "../cashregister.component";

import {
    Categories,
    Channels,
    Customers,
    Departments,
    Items,
    SalesCustomer
} from "tilby-models";
import {BehaviorSubject, Subject} from "rxjs";
import {Exit} from "@tilby/tilby-ui-lib/components/tilby-order";
import { isTablet, mobileCheck } from "@tilby/tilby-ui-lib/utilities";
import { ActiveSaleStoreService } from "./active-sale.store.service";
import {
    $state,
    addSelectCustomerDialog,
    eInvoicePlugin,
    radioListSelector,
    util
} from "app/ajs-upgraded-providers";

import { ActiveSaleService } from "./active-sale.service";
import {
    AddSelectCustomerDialogService,
    AlertDialogService,
    ConfirmDialogService,
    DocumentPrintersManagerDialogStateService,
} from "src/app/dialogs";
import { TilbyDatePipe } from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { parseItemsCollection } from "../utils";
import { SalesCashregister, SalesCustomerCashregister } from "src/app/shared/model/cashregister.model";

export type CashregisterTopbarSettingsFields = {
    categories: {category: string, category_grid_rows: number, category_grid_columns?: number, category_font_name_size: number, category_order_by: string},
    items: {item: string, item_icon: boolean, item_grid_columns: number, font_name_size: number, font_price_size: number},
    view: {view: string},
    order: {order: string},
    availability: {availability: string},
    search_in_all_category: {searchCategory: string},
    show_stock_level: {showStock: boolean},
    keyboard: {keyboard_dimension: KeyboardDimension, keyboard_font_size: number},
    cart: {expand_rows: string}
}

type Address = Partial<{ [prop in FilterStartingWith<keyof SalesCustomer, "shipping_">]: SalesCustomer[prop] | null } & Record<'shipping_address_id', number | null>>;

export type CashRegisterStockFormFieldsValue =
    {[prop in keyof Omit<CashregisterStockFormFields,'url'>]:CashregisterStockFormFields[prop]}
    &{[prop in keyof Pick<CashregisterStockFormFields,'url'>]?:CashregisterStockFormFields[prop]}
    &{ shop_id: string };

const DEFAULT_TOTAL_EXITS_NUMBER = 5;

export type CategoryView = 'tab' | 'grid';
export type ItemView = 'photoGrid' | 'noPhotoGrid' | 'tab' | 'list';
export type View = 'normal' | 'compact';
export type Order = '+name' | '+index' | '+price' | '-price';
export type CategoryOrder = '+name' | '+index';

export type EventsSettings =
  | { category: CategoryView }
  | { item: ItemView }
  | { view: View }
  | { order: Order }
  | { category_order_by: CategoryOrder }
  | { availability: boolean }
  | { search_in_all_categories: boolean }
  | { show_stock_quantity: boolean }
  | { category_grid_rows: number }
  | { category_grid_columns: number }
  | { item_grid_columns: number }

@Injectable({
    providedIn: 'root'
})
export class CashregisterStateService {
    private readonly activeSaleStoreService = inject(ActiveSaleStoreService);
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly entityDictionaries = inject(EntityDictionariesStoreService);
    private readonly injector = inject(Injector);
    private readonly screenOrientationService = inject(ScreenOrientationService);
    private readonly stateService = inject($state);
    private readonly translateService = inject(TranslateService);
    private readonly oldAddSelectCustomerDialog = inject(addSelectCustomerDialog);
    private readonly addSelectCustomerDialogService = inject(AddSelectCustomerDialogService);

    // CHECK MOBILE AND VERTICAL PORTRAIT
    public keyboardTotalValue = signal(0);
    public isMobilePortrait = this.screenOrientationService.isMobilePortrait;
    public isMobile = computed(() => this.screenOrientationService.getOrientation() && mobileCheck());
    private cashregisterStockForm?: CustomFormGroup<CustomForm<any>>;

    public deliveryChannels: Channels[]=[];
    public categories: Categories[]=[];
    public departments: Departments[]=[];
    public items?: ItemsByCategory;

    public exitsArray$ = new BehaviorSubject<Exit[]>([]);
    public lastUuidChanged$ = new BehaviorSubject<string|undefined>(undefined);
    public lastUuidSelected$ = new BehaviorSubject<string|undefined>(undefined);
    public resizeSplitView$ = new BehaviorSubject<number>(50);
    /** The keyboard asks the cart to set the quantity of the selected sale item. */
    public saleItemQuantity$ = new Subject<number>();

    public addItemToSale$ = new Subject<{
        item?: Items;
        combination_id?: number
        quantity?: number
        barcode?: string
    }>();

    public addNewItemToSale$ = new Subject<void>();
    public addNewItemToItems$ = new Subject<void>();

    public addDynamicCategoryItemToSale$ = new Subject<{
        category: Categories,
        price: number
    }>();

    public sideKeyboardPreference = signal(this.configurationManagerService.getPreference("cashregister.side_keyboard")||false);
    public sideKeyboard = computed(() => this.sideKeyboardPreference() && !this.screenOrientationService.isMobilePortrait());
    public displayMode = this.configurationManagerService.getPreference("showcase_display_mode") || 'normal';

    eventsSettings = new Subject<EventsSettings>();
    priceList = signal<number>(1, { equal: (a: number, b: number) => a === b });
    pricelistChangedEvent = new Subject<void>();

    // CASHREGISTER SEARCH
    public isShowSearch$ = new BehaviorSubject<{isSearch:boolean,searchValue:string}>({isSearch:false,searchValue:''});
    public keyboardIsOpen$ = new BehaviorSubject<boolean>(window.localStorage.getItem('cashregister::showKeyboard') === 'true');
    public keyboardIsOpenBySelection$ = new BehaviorSubject<boolean>(false);

    public isTablet=isTablet;
    public ticketViewActive = signal(false);

    public itemVisibleReorder?: Items[];
    public favoriteSelectedReorder = false;

    fontNameSizeSignal = signal(parseInt(this.configurationManagerService.getPreference('cashregister.font_name_size') || '12', 10));
    fontPriceSizeSignal = signal(parseInt(this.configurationManagerService.getPreference('cashregister.font_price_size') || '12', 10));
    categoryFontNameSizeSignal = signal(parseInt(this.configurationManagerService.getPreference('cashregister.category_font_name_size') || '12', 10));
    keyboardDimensionSignal = signal<KeyboardDimension>(this.configurationManagerService.getPreference('cashregister.keyboard_dimension') || "large");
    keyboardFontSizeSignal = signal(parseInt(this.configurationManagerService.getPreference('cashregister.keyboard_font_size') || '12', 10));
    expandCartRowsSignal = signal(!!this.configurationManagerService.getPreference('cashregister.expand_cart_rows'));
    showItemIconSignal = signal(this.configurationManagerService.getPreference('showcase_item_icon') || false);
    iconSizeSignal = signal(this.displayMode === 'normal' ? true : false);

    constructor(
    ) {
        ActiveSaleStoreService.saleUpdates$.subscribe((saleUpdates) => {
            this.updateSentExits();
            this.updateSaleStock(saleUpdates.currentSale);
        });
    }

    initState(param: { deliveryChannels: Channels[]; categories: Categories[]; departments: Departments[]; items: ItemsByCategory }) {
        this.deliveryChannels = param.deliveryChannels;
        this.categories = param.categories;
        this.departments = param.departments;
        this.items = param.items;

        this.resetExitArray();
    }

    // START - TILBY_SHOP_CART
    private getExitArray(): Exit[] {
        const totalExits = +((this.configurationManagerService.getPreference('orders.exits')) || DEFAULT_TOTAL_EXITS_NUMBER);

        // Get exits names
        let exitNames: Record<string,string> = {};

        try {
            exitNames = JSON.parse(this.configurationManagerService.getPreference('orders.exits_names') || '{}');
        } catch(err) {
        }

        const exitArray: Exit[] = [];

        for (let i = 0; i <= totalExits; i++) {
            exitArray.push({
                name: exitNames[i] || (i ? 'DIALOGS.EXIT_DIALOG.EXIT' : 'DIALOGS.EXIT_DIALOG.NO_EXIT_LABEL'),
                nameValue: i ? i : undefined,
                isSelected: !i
            });
        }

        return exitArray;
    }

    public resetExitArray() {
        this.updateSentExits(this.getExitArray());
    }

    private updateSentExits(exits?: Exit[]) {
        if(!exits) {
            exits = this.exitsArray$.getValue();
        }

        const sentExits = this.activeSaleStoreService.currentSale?.sent_exits || {};

        const updatedExits = exits.map(exit => {
            if (!exit.nameValue) {
                return exit;
            }

            return <Exit>{
                ...exit,
                status: sentExits[exit.nameValue] ? 'sent' : undefined
            }
        });

        this.exitsArray$.next(updatedExits);

        return updatedExits;
    }

    private updateSaleStock(sale?: SalesCashregister) {
        if (!sale) {
            return;
        }

        const saleItems = (sale.sale_items || []).filter(saleItem => saleItem.item_id);
        const stockRequirements: Record<string, number> = {};

        for (const saleItem of saleItems) {
            const target = saleItem.combination_id ? `combination_id_${saleItem.combination_id}` : `item_id_${saleItem.item_id}`;

            if (!stockRequirements[target]) {
                stockRequirements[target] = 0;
            }

            stockRequirements[target] += saleItem.quantity;
        }

        for (const saleItem of saleItems) {
            delete saleItem.$unavailable;

            const target: StockDictionaryKey = saleItem.combination_id ? `combination_id_${saleItem.combination_id}` : `item_id_${saleItem.item_id!}`;
            const currentStock = this.entityDictionaries.getStock(target);

            if(!currentStock) {
                continue;
            }

            if (currentStock.stock_quantity != null) { //Simple item
                if (currentStock.stock_quantity < stockRequirements[target]) {
                    saleItem.$unavailable = true;
                }
            } else { //BOM
                if (currentStock.available === 'unavailable') {
                    saleItem.$unavailable = true;
                }
            }
        }
    }

    // END - TILBY_SHOP_CART

    // START - FORM CREATIONS

    // FORM CASHREGISTER_STOCK_FORM
    createCashregisterStockForm(cashregisterStock: Partial<CashregisterStockFormFields> = new CashregisterStockFormFields(), hiddenFields?: Array<keyof CashregisterStockFormFields>) {
        const { sku, name, code, barcode } = cashregisterStock;

        const isCheckExternalStockEnabled = this.configurationManagerService.getPreference('cashregister.check_external_stock');

        this.cashregisterStockForm = new CustomFormGroup<CustomForm<CashRegisterStockFormFieldsValue>>({
            ...(isCheckExternalStockEnabled && {
                url: new CustomFormControl('I', {}, {
                    ...new CustomFormControlProps(),
                    inputType: 'radio',
                    label: '',
                    inputChoices: [
                        { value: "I", key: 'CASHREGISTER.CHECK_STOCK.INTERNAL' },
                        { value: "E", key: 'CASHREGISTER.CHECK_STOCK.EXTERNAL' }
                    ],
                    class: 'tw-w-full tw-flex-none tw-py-2.5 tw-flex-nowrap tw-gap-x-12'
                })
            }),
            shop_id: new CustomFormControl(null, {}, {
                ...new CustomFormControlProps(),
                label: 'CASHREGISTER.CHECK_STOCK.SHOP_ID',
                id: 'input-shop_id'
            }),
            sku: new CustomFormControl(sku || null, {}, {
                ...new CustomFormControlProps(),
                label: 'CASHREGISTER.CHECK_STOCK.SKU',
                id: 'input-sku'
            }),
            barcode: new CustomFormControl(barcode || null, {}, {
                ...new CustomFormControlProps(),
                label: 'CASHREGISTER.CHECK_STOCK.BARCODE',
                id: 'input-barcode',
                focusInitial: true
            }),
            code: new CustomFormControl(code || null, {}, {
                ...new CustomFormControlProps(),
                label: 'CASHREGISTER.CHECK_STOCK.CODE',
                id: 'input-code'
            }),
            name: new CustomFormControl(name || null, {}, {
                ...new CustomFormControlProps(),
                label: 'CASHREGISTER.CHECK_STOCK.NAME',
                id: 'input-name'
            }),
        });

        return this.cashregisterStockForm;
    }

    get url() {
        return this.cashregisterStockForm?.controls.url;
    }

    get shop_id() {
        return this.cashregisterStockForm?.controls.shop_id;
    }

    get sku() {
        return this.cashregisterStockForm?.controls.sku;
    }

    get barcode() {
        return this.cashregisterStockForm?.controls.barcode;
    }

    get code() {
        return this.cashregisterStockForm?.controls.code;
    }

    get name() {
        return this.cashregisterStockForm?.controls.name;
    }




    public setExit(exitSelected: Exit | undefined) {
        const newValue = this.exitsArray$.getValue().map(exit => ({
            ...exit,
            isSelected: exitSelected ? exit.nameValue == exitSelected.nameValue : false
        }));

        this.exitsArray$.next(newValue);
    }

    // END - FORM CREATIONS

    // START - MOBILE
    public setActionBarHeight(screenOrientation:OrientationType='portrait-primary',keyboardOffset=0){
        const actionBar=106;
        const topbar= this.isTablet?64:56;
        const margin = screenOrientation.startsWith('portrait')?4:16;
        return ((actionBar+margin+keyboardOffset)/(screen.height-topbar))*100;
    }
    public back() {
        if(this.ticketViewActive()){
            this.ticketViewActive.set(false);
        }
        else {
            this.stateService.go('app.new.cashregister.content.showcase',{},{inherit: false});
        }
    }
    // END - MOBILE

    public async addCustomer(customer: Customers | SalesCustomer) {
        const radioListSelectorService = this.injector.get(radioListSelector);
        const activeSaleService = this.injector.get(ActiveSaleService);
        const entityManagerService = this.injector.get(EntityManagerService);

        const saleCustomer = await activeSaleService.addCustomer(customer) as Customers & SalesCustomer;

        //Show customer notes if present
        if (!this.configurationManagerService.getPreference('cashregister.use_new_add_customer_dialog')) {
            if (customer.notes) {
                try {
                    await this.alertDialogService.openDialog({ data: { messageLabel: customer.notes } });
                } catch (err) { }
            }
        }

        //Ask to enable invoices if the customer has a valid billing address and the current printer supports them
        if (!this.configurationManagerService.getPreference('cashregister.disable_customer_invoice_prompt') && this.injector.get(eInvoicePlugin).isCustomerValid(saleCustomer)) {
            let currentDocData = activeSaleService.printerDocumentData;

            if ((typeof currentDocData == 'object') && !['e_invoice', 'summary_e_rc', 'summary_e_nrc', 'generic_invoice'].includes(currentDocData?.document_type?.id || '')) {
                try {
                    const docData = await this.injector.get(DocumentPrintersManagerDialogStateService).getPrinterDocumentData(currentDocData?.printer?.id || 'default', ['e_invoice', 'generic_invoice'], currentDocData?.options);
                    const answer = await this.confirmDialogService.openDialog({
                        data: {
                            messageLabel: 'CASHREGISTER.ACTIVE_SALE_MODEL.WANT_TO_ENABLE_INVOICE'
                        }
                    });

                    if (answer) {
                        activeSaleService.setPrinterDocumentData(docData);
                    }
                } catch (err) { }
            }
        }

        //Check if the customer has multiple shipping addresses and, if that's the case, ask which one is to use
        if (!this.configurationManagerService.getPreference('cashregister.use_new_add_customer_dialog')) {
            if (this.configurationManagerService.getPreference('cashregister.ask_shipping_address')) {
                let shippingAddressesCount = 0;
                const shippingAddresses: {
                    address: Address;
                    name: string;
                    idx?: number;
                    $disabled?: boolean;
                }[] = [{
                    address: {
                        shipping_prov: null,
                        shipping_street: null,
                        shipping_number: null,
                        shipping_zip: null,
                        shipping_city: null,
                        shipping_country: null,
                        shipping_address_id: null
                    },
                    name: this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.NO_DELIVERY'),
                    idx: -1
                }];
                for (let idx = 0; idx < 10; idx++) {
                    const slot = idx === 0 ? "" : `_${idx}` as ("" | "_2" | "_3" | "_4" | "_5" | "_6" | "_7" | "_8" | "_9");
                    if (!saleCustomer[`shipping_street${slot}`]) {
                        continue;
                    }
                    shippingAddressesCount++;
                    if (idx === 0) { //Set shipping_address_id to 0 in case the user cancels the selection afterwards
                        saleCustomer.shipping_address_id = 0;
                    }
                    const addressObj: Address = {
                        shipping_prov: saleCustomer[`shipping_prov${slot}`],
                        shipping_street: saleCustomer[`shipping_street${slot}`],
                        shipping_number: saleCustomer[`shipping_number${slot}`],
                        shipping_zip: saleCustomer[`shipping_zip${slot}`],
                        shipping_city: saleCustomer[`shipping_city${slot}`],
                        shipping_country: saleCustomer[`shipping_country${slot}`],
                        shipping_address_id: idx
                    };
                    if (idx) {
                        delete saleCustomer[`shipping_prov${slot}`];
                        delete saleCustomer[`shipping_street${slot}`];
                        delete saleCustomer[`shipping_number${slot}`];
                        delete saleCustomer[`shipping_zip${slot}`];
                        delete saleCustomer[`shipping_city${slot}`];
                        delete saleCustomer[`shipping_country${slot}`];
                    }
                    let addressStr = `${addressObj.shipping_street} ${addressObj.shipping_number || ''} ${addressObj.shipping_zip || ''} ${addressObj.shipping_city || ''} ${addressObj.shipping_prov ? `(${addressObj.shipping_prov})` : ''} `;
                    const isDisabled = this.configurationManagerService.getPreference('orders.allow_street_only_shipping_addresses') ? !addressObj.shipping_street : !addressObj.shipping_street || !addressObj.shipping_city;
                    if (isDisabled) {
                        addressStr += " (Incompleto)";
                    }
                    shippingAddresses.push({
                        address: addressObj,
                        name: addressStr,
                        $disabled: isDisabled
                    });
                }
                if (shippingAddressesCount >= 1) {
                    const addAction = {
                        callback: () => {
                            this.oldAddSelectCustomerDialog.show(activeSaleService.currentSale.sale_customer, { editMode: true })
                                .then((customer: Customers) => this.addCustomer(customer));
                        }
                    };
                    const sAddr = await radioListSelectorService.show(shippingAddresses, { label: this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.SELECT_SHIPPING_ADDRESS'), default: 0, addAction: addAction, hideCancel: true });
                    Object.assign(saleCustomer, sAddr.address);
                }
            }
            if (saleCustomer.fidelity) {
                const campaigns = await entityManagerService.campaigns.fetchCollectionOffline({ isValid: true });
                if (campaigns.length) {
                    const activeCampaign = campaigns[0];
                    const customerPointsArray = await entityManagerService.fidelitiesPoints.fetchCollectionOffline({ fidelity: saleCustomer.fidelity, campaign_id: activeCampaign?.id });
                    if (customerPointsArray?.length) {
                        const customerPoints = customerPointsArray[0];
                        const prizes = await entityManagerService.prizes.fetchCollectionOffline({ campaign_id: activeCampaign.id });
                        const now = TilbyDatePipe.date();
                        const giftPrizes = prizes.filter((prize) => prize.type === 'gift');
                        const giftsMap: Partial<Record<string, Items>> = {};
                        //Load prize items
                        for (let giftPrize of giftPrizes) {
                            const items = await entityManagerService.items.fetchCollectionOffline({ sku: giftPrize.item_sku });
                            if (items.length) {
                                giftsMap[giftPrize.item_sku || ''] = items[0];
                            }
                        }
                        const eligiblePrizes = prizes.filter((prize) => (((customerPoints.points || 0) >= prize.points) && (!prize.valid_from || TilbyDatePipe.compareDates(prize.valid_from.toString(), now) !== '>' && (!prize.valid_to || TilbyDatePipe.compareDates(prize.valid_to.toString(), now) !== '<'))));
                        if (eligiblePrizes?.length) {
                            const radioListPrizes = eligiblePrizes.map((prize) => {
                                const prizeName = [
                                    this.translateService.instant('FIDELITY.PRIZES.PRIZE_DIALOG.PRIZE_POINTS', { points: prize.points }),
                                    prize.name
                                ];
                                switch (prize.type) {
                                    case 'discount_fix':
                                        prizeName.push(this.translateService.instant('FIDELITY.PRIZES.PRIZE_DIALOG.DISCOUNT', { value: this.injector.get(TilbyCurrencyPipe).transform(prize.discount_amount || 0) }));
                                        break;
                                    case 'discount_perc':
                                        prizeName.push(this.translateService.instant('FIDELITY.PRIZES.PRIZE_DIALOG.DISCOUNT', { value: this.injector.get(util).round(prize.discount_amount) + '%' }));
                                        break;
                                    case 'gift':
                                        prizeName.push(this.translateService.instant('FIDELITY.PRIZES.PRIZE_DIALOG.GIFT', { value: giftsMap?.[prize.item_sku || '']?.name ?? '' }));
                                        break;
                                    default: break;
                                }
                                return {
                                    name: prizeName.join(' - '),
                                    prize: prize
                                };
                            });
                            try {
                                const selectedOption = await radioListSelectorService.show(radioListPrizes, { label: this.translateService.instant('FIDELITY.PRIZES.PRIZE_DIALOG.TITLE', { points: customerPoints.points }) });
                                await activeSaleService.addPrize(selectedOption.prize);
                            } catch (err) { }
                        }
                    }
                }
            }

            if (saleCustomer.default_pricelist) {
                activeSaleService.determinePricelistToUse(activeSaleService.currentSale);
            }
        }
    }

    public async getItems() {
        this.items = await this.injector.get(EntityManagerService).items.fetchCollectionOffline({on_sale: true}).then(items=>parseItemsCollection(items));
        return this.items;
    }

    setItemVisibleReorder(itemVisibleReorder: Items[]) {
        this.itemVisibleReorder = itemVisibleReorder;
    }

    setFavoriteSelectedReorder(favoriteSelectedReorder: boolean) {
        this.favoriteSelectedReorder = favoriteSelectedReorder;
    }

    async addCustomerAction() {
        const activeSaleService = this.injector.get(ActiveSaleService);

        if(!activeSaleService.isActiveSale()) {
            return;
        }

        activeSaleService.lockPaymentButtons.set(true);

        try {
            if(this.configurationManagerService.getPreference('cashregister.use_new_add_customer_dialog')) {
                await this.openSelectCustomerDialog(activeSaleService.currentSale.sale_customer);
            } else {
                const customer = await this.oldAddSelectCustomerDialog.show(activeSaleService.currentSale.sale_customer);

                await this.addCustomer(customer);
            }
        } catch(err) {
        } finally {
            activeSaleService.lockPaymentButtons.set(false);
        }
    }

    async openSelectCustomerDialog(currentCustomer?: SalesCustomerCashregister){
        const customer = await this.addSelectCustomerDialogService.openDialog({
            data: {
                currentCustomer,
                options: {
                    onlineMode: false
                }
            }
        });

        if(customer) {
            const activeSaleService = this.injector.get(ActiveSaleService);

            if(customer.sale_customer){
                await this.addCustomer(customer.sale_customer);
            } else {
                activeSaleService.removeCustomer();
            }

            if(customer.prize){
                await activeSaleService.addPrize(customer.prize);
            }

            if(customer.default_pricelist) {
                activeSaleService.determinePricelistToUse(activeSaleService.currentSale, true, customer.default_pricelist.applyNewPricelist);
            }
        }
    }
}
